import React from 'react';
import {
    ALL_MARKET_COUNTRIES,
    MARKET_COUNTRY_OPTIONS,
    formatMarketCountries,
    normalizeMarketCountries,
} from '../../helpers/marketSettings';
import './MarketCountryControls.css';

function CountryChecklist({
    idPrefix,
    legend,
    countries,
    onChange,
    disabled,
    help,
}) {
    const selectedCountries = normalizeMarketCountries(countries, []);

    function toggleCountry(countryCode, checked) {
        const selected = new Set(selectedCountries);
        if (checked) selected.add(countryCode);
        else selected.delete(countryCode);
        onChange([...selected]);
    }

    return (
        <fieldset className="group market-country-controls__countries">
            <legend>{legend}</legend>
            {MARKET_COUNTRY_OPTIONS.map(([value, label]) => (
                <label className="checkbox" htmlFor={`${idPrefix}-${value}`} key={value}>
                    <input
                        id={`${idPrefix}-${value}`}
                        type="checkbox"
                        value={value}
                        checked={selectedCountries.includes(value)}
                        disabled={disabled}
                        onChange={event => toggleCountry(value, event.target.checked)}
                    />
                    <span>{label}</span>
                </label>
            ))}
            {help && <p className="help">{help}</p>}
        </fieldset>
    );
}

export default function MarketCountryControls({
    idPrefix,
    disabled = false,
    shipToCountries,
    onShipToCountriesChange,
    shipToHelp,
    inheritShipTo = false,
    inheritedShipToCountries = [],
    onInheritShipToChange,
    showAvailability = false,
    availableCountries,
    restrictedCountries,
    onAvailabilityChange,
}) {
    const isAvailableEverywhere = Array.isArray(availableCountries)
        && availableCountries.some(country => (
            String(country).trim().toUpperCase() === ALL_MARKET_COUNTRIES
        ));
    const availabilityMode = isAvailableEverywhere
        ? (Array.isArray(restrictedCountries) ? 'except' : 'everywhere')
        : (Array.isArray(availableCountries) ? 'only' : 'home');
    const displayedShipToCountries = inheritShipTo
        ? inheritedShipToCountries
        : shipToCountries;

    function updateAvailabilityMode(mode) {
        if (mode === 'only') {
            onAvailabilityChange({availableCountries: [], restrictedCountries: null});
        } else if (mode === 'except') {
            onAvailabilityChange({
                availableCountries: [ALL_MARKET_COUNTRIES],
                restrictedCountries: [],
            });
        } else if (mode === 'everywhere') {
            onAvailabilityChange({
                availableCountries: [ALL_MARKET_COUNTRIES],
                restrictedCountries: null,
            });
        } else {
            onAvailabilityChange({availableCountries: null, restrictedCountries: null});
        }
    }

    return (
        <div className="market-country-controls">
            {onShipToCountriesChange && (
                <>
                    {onInheritShipToChange && (
                        <div className="group">
                            <label className="checkbox" htmlFor={`${idPrefix}-inherit-ship-to`}>
                                <input
                                    id={`${idPrefix}-inherit-ship-to`}
                                    type="checkbox"
                                    checked={inheritShipTo}
                                    disabled={disabled}
                                    onChange={event => (
                                        onInheritShipToChange(event.target.checked)
                                    )}
                                />
                                <span>Use storefront ship-to countries</span>
                            </label>
                            <p className="help">
                                Keep this product in sync with the storefront default.
                            </p>
                        </div>
                    )}
                    {onInheritShipToChange && inheritShipTo ? (
                        <p className="help market-country-controls__inherited">
                            Storefront destinations:{' '}
                            <strong>{formatMarketCountries(inheritedShipToCountries)}</strong>
                        </p>
                    ) : (
                        <CountryChecklist
                            idPrefix={`${idPrefix}-ship-to`}
                            legend="Ship-to Countries"
                            countries={displayedShipToCountries}
                            onChange={onShipToCountriesChange}
                            disabled={disabled}
                            help={shipToHelp}
                        />
                    )}
                </>
            )}

            {showAvailability && (
                <div
                    className="group market-country-controls__availability"
                    role="group"
                    aria-labelledby={`${idPrefix}-availability-label`}
                >
                    <p id={`${idPrefix}-availability-label`}>
                        <strong>Product Availability</strong>
                    </p>
                    <p className="help">
                        Use this for legal, licensing, or compliance limits. Shipping destinations
                        are configured separately for physical products.
                    </p>
                    {[
                        ['home', 'Available only in the storefront country'],
                        ['everywhere', 'Available in every supported country'],
                        ['only', 'Available only in selected countries'],
                        ['except', 'Available everywhere except selected countries'],
                    ].map(([value, label]) => (
                        <label
                            className="radio-option"
                            htmlFor={`${idPrefix}-availability-${value}`}
                            key={value}
                        >
                            <input
                                id={`${idPrefix}-availability-${value}`}
                                type="radio"
                                name={`${idPrefix}-availability-mode`}
                                value={value}
                                checked={availabilityMode === value}
                                disabled={disabled}
                                onChange={() => updateAvailabilityMode(value)}
                            />
                            <span>{label}</span>
                        </label>
                    ))}
                    {availabilityMode === 'only' && (
                        <CountryChecklist
                            idPrefix={`${idPrefix}-available`}
                            legend="Available Countries"
                            countries={availableCountries}
                            onChange={countries => onAvailabilityChange({
                                availableCountries: countries,
                                restrictedCountries: null,
                            })}
                            disabled={disabled}
                        />
                    )}
                    {availabilityMode === 'except' && (
                        <CountryChecklist
                            idPrefix={`${idPrefix}-restricted`}
                            legend="Excluded Countries"
                            countries={restrictedCountries}
                            onChange={countries => onAvailabilityChange({
                                availableCountries: [ALL_MARKET_COUNTRIES],
                                restrictedCountries: countries,
                            })}
                            disabled={disabled}
                        />
                    )}
                </div>
            )}
        </div>
    );
}
